import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

import AdminModal from '../../../reusable/admin-modal/admin-modal.component.jsx';
import Button from '../../../reusable/button/button.component.jsx';
import DynamicProductFields from '../../../reusable/inputs/dynamic-product-fields/dynamic-product-fields.component.jsx';
import Spinner from '../../../reusable/spinner/spinner.component.jsx';

import { ToastContext } from '../../../../contexts/toast.context.jsx';
import { api } from '../../../../config.js';
import Client from '../../../../tools/client.js';

import {
  ButtonContainer,
  ButtonRowContainer,
  DeleteButton,
  InputContainer,
  MainContainer,
  ProductContent,
  UpdateInput,
  UpdateLabel,
  UpdateTextarea,
} from './update-product.styles.jsx';

const client = new Client();

const UpdateProduct = ({ product, getProduct, setShowUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [productTypeSelectData, setProductTypeSelectData] = useState([]);
  const [productType, setProductType] = useState(product.ProductType || null);
  const [productDefaultImage, setProductDefaultImage] = useState('');
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description);
  const [dynamicFields, setDynamicFields] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { errorToast, successToast } = useContext(ToastContext);

  useEffect(() => {
    if (product.ProductImages.length !== 0) {
      const mainImage = product.ProductImages.find(image => image.position === 1);
      if (mainImage) setProductDefaultImage(mainImage);
    }

    getProductTypes();

    // Prefill dynamic fields on mount
    if (product.ProductType?.ProductTypeFields) {
      const prefill = {};
      product.ProductType.ProductTypeFields.forEach(field => {
        const val = product.details?.[field.key];
        if (val !== undefined) {
          prefill[field.key] = val;
        }
      });
      setDynamicFields(prefill);
    }
  }, []);

  const getProductTypes = async () => {
    setLoading(true);
    const res = await client.getProductTypes();
    setProductTypeSelectData(res);
    setLoading(false);
  };

  const handleDynamicFieldChange = (key, value) => {
    setDynamicFields(prev => ({ ...prev, [key]: value }));
  };

  const updateProduct = async () => {
    setLoading(true);

    const params = {
      id: product.id,
      categoryId: product.categoryId,
      productTypeId: productType?.id || null,
      name,
      description,
      details: dynamicFields
    };

    const res = await client.updateProduct(params);

    if (res.status === 400) {
      setLoading(false);
      errorToast(res.message);
      return;
    }

    successToast('Product Updated');
    await getProduct();
    setLoading(false);
    setShowUpdate(false);
  };

  const confirmDelete = () => setShowDeleteModal(true);

  const deleteProduct = async () => {
    const res = await client.deleteProduct({ id: product.id });
    if (res.status) {
      setShowDeleteModal(false);
      errorToast(res.message);
      return;
    }

    navigate('/products');
  };

  return (
    <MainContainer>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <AdminModal
            show={showDeleteModal}
            setShow={setShowDeleteModal}
            title={'Delete Product'}
            image={productDefaultImage ? api + productDefaultImage.path : ''}
            message={`Are you sure you want to delete ${name}, its inventory and all associated images forever?`}
            action={deleteProduct}
            actionText={'Delete'}
          />
          <ProductContent>
            <InputContainer>
              <UpdateLabel>Name:</UpdateLabel>
              <UpdateInput value={name} onChange={e => setName(e.target.value)} />
            </InputContainer>

            <InputContainer>
              <UpdateLabel>Description:</UpdateLabel>
              <UpdateTextarea value={description} onChange={e => setDescription(e.target.value)} />
            </InputContainer>

            <InputContainer>
              <UpdateLabel>Product Type:</UpdateLabel>
              <Select
                value={
                  productType && Array.isArray(productTypeSelectData)
                    ? productTypeSelectData.find(pt => pt.id === productType.id)
                    : null
                }
                onChange={selected => {
                  setProductType(selected);
                  const nextFields = {};
                  if (selected?.ProductTypeFields) {
                    selected.ProductTypeFields.forEach(field => {
                      const existing = product.details?.[field.key];
                      if (existing !== undefined) {
                        nextFields[field.key] = existing;
                      }
                    });
                  }
                  setDynamicFields(nextFields);
                }}
                options={productTypeSelectData}
                getOptionLabel={e => e.name}
                getOptionValue={e => e.id}
                placeholder="Select a product type..."
                isClearable
              />
            </InputContainer>

            {productType?.ProductTypeFields?.length > 0 && (
              <DynamicProductFields
                fields={productType.ProductTypeFields}
                values={dynamicFields}
                setValues={setDynamicFields}
              />
            )}
          </ProductContent>

          <ButtonContainer>
            <DeleteButton onClick={confirmDelete}>DELETE</DeleteButton>
            <ButtonRowContainer>
              <Button onClick={() => setShowUpdate(false)}>Cancel</Button>
              <Button onClick={updateProduct}>Update</Button>
            </ButtonRowContainer>
          </ButtonContainer>
        </>
      )}
    </MainContainer>
  );
};

export default UpdateProduct;